import _BookShelfQuery from "../__generated__/BookShelfQuery.graphql";
import { useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Link } from 'react-router-dom';
import { BookOpen, Check, Star } from 'lucide-react';
import { En } from '../i18n/En';
import { t } from '../i18n/t';
import { registerFor } from '../profile/register';
import { EmptyState } from './EmptyState';
import { finishedMark } from './storyReader.ts';
import type { BookShelfQuery as BookShelfQueryType } from '../__generated__/BookShelfQuery.graphql';
const BookShelfQuery = (_BookShelfQuery.hash && _BookShelfQuery.hash !== "49557a3310231faba5f5863ecf9029fe" && console.error("The definition of 'BookShelfQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _BookShelfQuery);

/**
 * The drawn cover, falling back to the emoji `cover` on a failed load
 * (amendment 2026-09-29). The failure is the whole reason this is its own
 * component: `onError` needs state to remember it happened.
 */
function Cover({
  src,
  emoji
}: {
  src: string;
  emoji: string;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <span aria-hidden className="bg-primary/10 grid aspect-[4/3] w-full place-items-center text-6xl leading-none">
        {emoji}
      </span>;
  }
  return <img src={src} alt="" aria-hidden className="aspect-[4/3] w-full object-cover" loading="lazy" onError={() => setFailed(true)} />;
}

/**
 * The shelf (storybooks PRD §5.1). A finished book carries a sticker; an
 * unopened one carries nothing: no count, no "unread", no due marker. Both
 * registers (amendment 2026-09-29). The server decides which books a
 * register sees, and this marks a finished one with a star for a child and a
 * check for an adult.
 */
export function BookShelf() {
  // The child comes back here after a book to find its sticker.
  const data = useLazyLoadQuery<BookShelfQueryType>(BookShelfQuery, {}, {
    fetchPolicy: 'store-and-network'
  });
  const learner = data.me?.learner;
  const register = registerFor(learner?.defaultCharacter?.audience);
  const books = learner?.storyBooks ?? [];
  return <div className="flex w-full flex-col gap-4 p-4">
      <h1 className="text-2xl font-bold">{t('books-title')}</h1>
      {books.length === 0 && <EmptyState icon={BookOpen} body={t('books-empty')} />}
      <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
        {books.map(b => <li key={b.id}>
            <Link to={`/books/${encodeURIComponent(b.id)}`} className="bg-base-100 border-base-200 hover:border-primary/40 relative flex h-full min-h-(--hit-min) flex-col overflow-hidden rounded-box border shadow-sm transition-shadow hover:shadow-md">
              <Cover src={b.coverImage} emoji={b.cover} />
              <span className="flex flex-1 items-center justify-center p-3 text-center leading-tight font-semibold">
                <En>{b.title}</En>
              </span>
              {/* Out of the flow, so a finished card is the same height as
                  an unfinished one and the grid doesn't shift when it lands. */}
              {b.finished && <span className="bg-base-100/90 absolute end-2 top-2 rounded-full p-1 shadow">
                  {finishedMark(register) === 'star' ? <Star role="img" aria-label={t('books-finished')} className="text-warning size-6 fill-current" /> : <Check role="img" aria-label={t('books-finished')} className="text-success size-6" />}
                </span>}
            </Link>
          </li>)}
      </ul>
    </div>;
}